/**
 * DebugAssignmentsPanel — Modal con JSON exportable para diagnóstico de
 * asignaciones (motor v2 + legacy fallback). Patrón inspirado en
 * `DebugInfoPanel` de MassCampaignsPage.
 *
 * Uso:
 *   const [debugOpen, setDebugOpen] = useState(false);
 *   const [debugJson, setDebugJson] = useState(null);
 *   ...
 *   <DebugAssignmentsPanel
 *     open={debugOpen}
 *     onClose={() => setDebugOpen(false)}
 *     debugJson={debugJson}
 *   />
 *
 * Para construir el JSON desde un caller, llamar `buildAssignmentsDebugJson`.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Info } from 'lucide-react';
import { isCarDealerUser, isDeveloperUser } from '@/lib/companyVertical';

export default function DebugAssignmentsPanel({ open, onClose, debugJson, title = 'Debug Info — Asignaciones' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(debugJson, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback para sandboxes sin permiso de clipboard
      try {
        const ta = document.createElement('textarea');
        ta.value = JSON.stringify(debugJson, null, 2);
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* ignore */ }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="debug-assignments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info size={16} />
            {title}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Copia este JSON para diagnóstico con Codex
          </p>
        </DialogHeader>
        <div className="relative">
          <Button
            size="sm"
            variant="outline"
            className="absolute top-2 right-2 text-xs z-10"
            onClick={handleCopy}
            data-testid="debug-assignments-copy-btn"
          >
            {copied ? '✓ Copiado' : '📋 Copiar JSON'}
          </Button>
          <pre
            className="bg-black/40 rounded-lg p-4 text-xs text-green-300 overflow-x-auto mt-2 font-mono whitespace-pre-wrap"
            data-testid="debug-assignments-panel"
          >
            {debugJson ? JSON.stringify(debugJson, null, 2) : 'No hay datos cargados.'}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helper: construye el JSON de diagnóstico ────────────────────────
// extras: { products_count, developments_count } opcional para incluir
//         contadores cuando se llama desde GroupsPage.
export function buildAssignmentsDebugJson({ groups = [], users = [], user = null, rrConfig = {}, recentLeads = [], extras = {} }) {
  const hostname = (typeof window !== 'undefined' && window.location?.hostname) || '';
  const env = hostname.includes('lab') || hostname.includes('staging') ? 'lab' : 'prod';
  const db = env === 'lab' ? 'dealider_staging_db' : 'dealider_db';

  const nowCDMX = new Date().toLocaleString('sv-SE', { timeZone: 'America/Mexico_City' }).replace(' ', 'T') + ' CDMX';

  const vertical = isCarDealerUser(user)
    ? 'car_dealer'
    : isDeveloperUser(user)
      ? 'developer'
      : (user?.industry || user?.business_type || 'unknown');

  // Día de hoy en CDMX (key en inglés para mapear contra schedule)
  const todayKey = new Date().toLocaleDateString('en-US', { weekday: 'long', timeZone: 'America/Mexico_City' }).toLowerCase();

  const active = Array.isArray(rrConfig?.active_vendors) ? rrConfig.active_vendors : [];
  const idx = rrConfig?.current_index ?? 0;
  const nextGlobalVid = active[idx] || null;

  return {
    generated_at: new Date().toISOString(),
    env,
    db,
    company: {
      id: user?.company_id || '',
      name: user?.company?.name || '',
      business_type: user?.business_type || '',
      industry: user?.industry || '',
      vertical_detected: vertical,
    },
    motor: {
      version: 'v2+legacy_fallback',
      timezone: 'America/Mexico_City (CDMX UTC-6)',
      current_time_cdmx: nowCDMX,
    },
    round_robin_global: {
      active_vendors_count: active.length,
      current_index: idx,
      next_vendor_id: nextGlobalVid,
      next_vendor_name: users.find(u => u.id === nextGlobalVid)?.name || null,
      active_vendors: active.map(vid => ({
        id: vid,
        name: users.find(u => u.id === vid)?.name || vid,
      })),
    },
    groups: groups.map(g => ({
      id: g.id,
      name: g.name,
      is_active: g.is_active,
      current_index: g.current_index ?? 0,
      specialty: g.specialty || {},
      members_count: (g.members || []).length,
      members: (g.members || []).map(m => {
        const u = users.find(x => x.id === m.vendor_id);
        const sched = m.schedule?.[todayKey];
        return {
          vendor_id: m.vendor_id,
          vendor_name: u?.name || 'Desconocido',
          receives_leads: u?.receives_leads ?? false,
          is_active: u?.is_active ?? false,
          schedule_today: sched ? {
            day: todayKey,
            active: sched.active,
            start_hour: sched.start_hour,
            end_hour: sched.end_hour,
          } : null,
          available_now: !!sched?.active,
        };
      }),
      next_in_roulette: (() => {
        const members = g.members || [];
        if (!members.length) return null;
        const i = (g.current_index ?? 0) % members.length;
        const m = members[i];
        const u = users.find(x => x.id === m?.vendor_id);
        return m ? { vendor_id: m.vendor_id, vendor_name: u?.name || 'Desconocido' } : null;
      })(),
    })),
    recent_assignments_last_10: (Array.isArray(recentLeads) ? recentLeads : [])
      .slice(0, 10)
      .map(l => ({
        lead_id: l.id,
        lead_name: l.name || '',
        assigned_to_id: l.assigned_to || null,
        assigned_to_name: users.find(u => u.id === l.assigned_to)?.name || null,
        level: l.assignment_trace?.level || null,
        group_id: l.assignment_trace?.group_id || null,
        campaign_id: l.campaign_id || null,
        created_at: l.created_at,
      })),
    ...(extras.products_count != null ? { products_count: extras.products_count } : {}),
    ...(extras.developments_count != null ? { developments_count: extras.developments_count } : {}),
    codex_query_hint: [
      `find leads donde company_id="${user?.company_id || ''}"`,
      'ordenado por created_at desc limit 20.',
      'Para asignaciones por grupo: agrupa por',
      'assignment_trace.group_id y cuenta.',
      'Para estado del motor: find roulette_groups donde',
      `company_id="${user?.company_id || ''}"`,
      'proyectar id,name,current_index,members,specialty.',
      'Para round_robin global: find round_robin donde',
      `company_id="${user?.company_id || ''}".`,
    ].join(' '),
  };
}
